import React, { useMemo } from "react";
import { getText } from "../Utlities/app-store";

// Configuration: How many pages to show on either side of the current page
const PAGE_RANGE_DISPLAY = 2;

const Pagination = ({ TotalRecords, Take, Page, onPageChange, theme="" }) => {
  // --- 1. Calculation Logic ---
  const { totalItems, pageSize, currentPage, totalPages, startItem, endItem } =
    useMemo(() => {
      const totalItems = TotalRecords;
      const pageSize = Take > 0 ? Take : 10;
      const currentPage = Page < 0 ? 0 : Page; // 0-based

      const totalPages = pageSize === 0 ? 0 : Math.ceil(totalItems / pageSize);

      const startItem = totalItems === 0 ? 0 : currentPage * pageSize + 1;
      const endItem = Math.min((currentPage + 1) * pageSize, totalItems);

      return {
        totalItems,
        pageSize,
        currentPage,
        totalPages,
        startItem,
        endItem,
      };
    }, [TotalRecords, Take, Page]);

  // Only render if there is more than one page
  if (totalPages <= 1) {
    return null;
  }

  // --- 2. Standardized Page Number Generation Logic ---
  const getPageItems = () => {
    const pages = [];
    const lastPage = totalPages - 1;
    const boundary = PAGE_RANGE_DISPLAY; // Pages around the current page

    // Function to add a page if it's not the last one added
    const addPage = (p) => {
      if (pages.length === 0 || pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };

    // Function to add ellipsis if needed
    const addEllipsis = () => {
      if (pages[pages.length - 1] !== "ellipsis") {
        pages.push("ellipsis");
      }
    };

    for (let i = 0; i < totalPages; i++) {
      const isStart = i < boundary;
      const isEnd = i > lastPage - boundary;
      const isCurrentWindow =
        i >= currentPage - boundary && i <= currentPage + boundary;

      if (isStart || isEnd || isCurrentWindow) {
        // Show pages at the start, at the end, or in the current window
        addPage(i);
      } else if (
        i === currentPage - boundary - 1 ||
        i === currentPage + boundary + 1
      ) {
        // Insert ellipsis when outside the window, but before the next visible page
        addEllipsis();
      }
    }
    return pages;
  };

  const visiblePageItems = getPageItems();

  // --- 3. Click Handler ---
  const handlePageClick = (pageIndex) => (event) => {
    event.preventDefault();
    if (pageIndex >= 0 && pageIndex < totalPages) {
      onPageChange(pageIndex);
    }
  };

  // --- 4. Render JSX ---
  return (
    <div className="">
      <nav aria-label="Table pagination">
        <ul className="pagination justify-content-center mb-0">
          {/* Previous Button */}
          {currentPage > 0 && (
            <>
              <li className={`page-item`}>
                <a
                  className="page-link"
                  href="#"
                  onClick={handlePageClick(0)}
                  aria-disabled={currentPage === 0}
                  aria-label="Previous"
                  title={getText("firstPage")}
                >
                  <span aria-hidden="true" className="no-event">
                    <i class="fa-solid fa-angles-left"></i>
                  </span>
                </a>
              </li>
              <li className={`page-item`}>
                <a
                  className="page-link"
                  href="#"
                  onClick={handlePageClick(currentPage - 1)}
                  aria-disabled={currentPage === 0}
                  aria-label="Previous"
                  title={getText("previousPage")}
                >
                  <span aria-hidden="true" className="no-event">
                    <i class="fa-solid fa-chevron-left"></i>
                  </span>
                </a>
              </li>
            </>
          )}

          {/* Page Numbers and Ellipses */}
          {visiblePageItems.map((item, index) => {
            // Render Ellipsis
            if (item === "ellipsis") {
              return (
                <li key={`ellipsis-${index}`} className="page-item disabled">
                  <span className="page-link">...</span>
                </li>
              );
            }

            // Render Page Number (i is the 0-based page index)
            const i = item;
            const isActive = i === currentPage;
            return (
              <li key={i} className={`page-item`}>
                <a
                  className={`page-link  ${isActive ? "text-white "+theme : ""}`}
                  href="#"
                  onClick={handlePageClick(i)}
                  aria-current={isActive ? "page" : undefined}
                >
                  {i + 1} {/* display 1-based */}
                </a>
              </li>
            );
          })}

          {/* Next Button */}
          {!(currentPage >= totalPages - 1) && (
            <>
              <li className={`page-item`}>
                <a
                  className="page-link"
                  href="#"
                  onClick={handlePageClick(currentPage + 1)}
                  aria-disabled={currentPage >= totalPages - 1}
                  aria-label="Next"
                  title={getText("nextPage")}
                >
                  <span aria-hidden="true" className="no-event">
                    <i class="fa-solid fa-chevron-right"></i>
                  </span>
                </a>
              </li>
              <li className={`page-item`}>
                <a
                  className="page-link"
                  href="#"
                  onClick={handlePageClick(totalPages - 1)}
                  aria-disabled={currentPage >= totalPages - 1}
                  aria-label="Next"
                  title={getText("lastPage")}
                >
                  <span aria-hidden="true" className="no-event">
                    <i class="fa-solid fa-angles-right"></i>
                  </span>
                </a>
              </li>
            </>
          )}
        </ul>
      </nav>

      <div className="text-center mt-2 small text-muted">
        {getText("showing")} {startItem}–{endItem} {getText("outof")}{" "}
        {totalItems}
      </div>
    </div>
  );
};

export default Pagination;
